import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TransferBooking {
  id: string;
  booking_number: string;
  full_name: string;
  email: string;
  phone: string;
  from_location: string;
  to_location: string;
  passengers: number;
  trip_type: 'oneway' | 'roundtrip';
  service_type: 'standard' | 'vip';
  total_price: number;
  arrival_date?: string;
  arrival_time?: string;
  airline?: string;
  flight_number?: string;
  return_date?: string;
  return_time?: string;
  return_airline?: string;
  return_flight_number?: string;
  amount_of_luggage?: string;
  remarks?: string;
  extra_items?: Array<{
    extra_item_id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
  extras_cost?: number;
  payment_method: 'cash' | 'paypal' | 'stripe';
  payment_id?: string;
  payment_status: 'pending' | 'completed' | 'failed';
  booking_status: 'pending' | 'confirmed' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export const useBookings = () => {
  const [bookings, setBookings] = useState<TransferBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('transfer_bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings((data || []) as TransferBooking[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Error loading bookings');
    } finally {
      setLoading(false);
    }
  };

  const createBooking = async (bookingData: {
    full_name: string;
    email: string;
    phone: string;
    from_location: string;
    to_location: string;
    passengers: number;
    trip_type: 'oneway' | 'roundtrip';
    service_type: 'standard' | 'vip';
    total_price: number;
    arrival_date?: string;
    arrival_time?: string;
    airline?: string;
    flight_number?: string;
    return_date?: string;
    return_time?: string;
    return_airline?: string;
    return_flight_number?: string;
    amount_of_luggage?: string;
    remarks?: string;
    extra_items?: Array<{
      extra_item_id: string;
      name: string;
      price: number;
      quantity: number;
    }>;
    extras_cost?: number;
    payment_method: 'cash' | 'paypal' | 'stripe';
    payment_status: 'pending' | 'completed' | 'failed';
    booking_status: 'pending' | 'confirmed' | 'cancelled';
  }) => {
    try {
      // Generate booking number client-side to avoid RLS issues
      const timestamp = Date.now();
      const randomDigits = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const clientBookingNumber = `TRF-${timestamp.toString().slice(-6)}${randomDigits}`;

      const insertData = {
        ...bookingData,
        booking_number: clientBookingNumber
      };

      const { error } = await supabase
        .from('transfer_bookings')
        .insert(insertData);

      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      // Only send confirmation email for non-PayPal payments
      // PayPal payments will send email after successful payment verification
      if (insertData.payment_method !== 'paypal') {
        console.log('Sending confirmation email for booking:', insertData.booking_number);
        try {
          await sendConfirmationEmail(insertData);
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't throw here - we still want the booking to be created even if email fails
        }
      } else {
        console.log('PayPal payment detected, skipping immediate email confirmation');
      }
      
      await fetchBookings(); // Refresh the list
      
      // Return the booking data we just created
      return {
        id: crypto.randomUUID(), // Generate a temporary ID for navigation
        ...insertData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as TransferBooking;
    } catch (err: any) {
      console.error('Error creating booking:', err);
      console.error('Error details:', {
        message: err?.message,
        details: err?.details,
        hint: err?.hint,
        code: err?.code
      });
      throw new Error(`Error creating booking: ${err?.message || 'Unknown error'}`);
    }
  };

  const sendConfirmationEmail = async (bookingData: any) => {
    try {
      // Get email configuration and transfer template using correct keys
      const [emailEnabledResult, adminEmailResult, senderEmailResult, senderNameResult, templateResult] = await Promise.all([
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_enabled')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_admin_email')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_sender_email')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_sender_name')
          .single(),
        supabase
          .from('site_config')
          .select('value')
          .eq('key', 'email_template_transfer')
          .single()
      ]);

      const emailEnabled = emailEnabledResult.data?.value;
      const rawAdminEmail = adminEmailResult.data?.value;
      const rawSenderEmail = senderEmailResult.data?.value;
      const rawSenderName = senderNameResult.data?.value;
      const template = templateResult.data?.value as any;

      const normalize = (v: any) =>
        typeof v === 'string' ? v.trim().replace(/^(["'])(.*)\1$/g, '$2') : v;

      const adminEmail = normalize(rawAdminEmail);
      const senderEmail = normalize(rawSenderEmail);
      const senderName = normalize(rawSenderName);

      console.log('Email configuration:', { emailEnabled, adminEmail, senderEmail, senderName });
      console.log('Template:', template);

      if (!template?.is_active) {
        console.log('Transfer email template is not active, skipping email');
        return;
      }

      if (!emailEnabled) {
        console.log('Email service is not enabled, skipping email');
        return;
      }

      // Call the edge function to send confirmation email
      console.log('Calling send-booking-confirmation edge function...');
      
      // Format extra items for email
      const extraItemsText = bookingData.extra_items && bookingData.extra_items.length > 0
        ? bookingData.extra_items.map((item: any) => 
            `${item.name} x${item.quantity} - $${(item.price * item.quantity).toFixed(2)}`
          ).join(', ')
        : 'Ninguno';
      
      const extrasCostText = bookingData.extras_cost 
        ? `$${bookingData.extras_cost.toFixed(2)}` 
        : '$0.00';

      const { data, error } = await supabase.functions.invoke('send-booking-confirmation', {
        body: {
          booking_data: {
            booking_number: bookingData.booking_number,
            customer_name: bookingData.full_name,
            email: bookingData.email,
            phone: bookingData.phone,
            from_location: bookingData.from_location,
            to_location: bookingData.to_location,
            passengers: bookingData.passengers.toString(),
            trip_type: bookingData.trip_type === 'oneway' ? 'Solo ida' : 'Ida y vuelta',
            service_type: bookingData.service_type === 'standard' ? 'Estándar' : 'VIP',
            total_price: bookingData.total_price.toFixed(2),
            extra_items: extraItemsText,
            extras_cost: extrasCostText,
          },
          template: {
            subject: template.subject,
            html_content: template.html_content,
            variables: template.variables
          },
          admin_email: adminEmail,
          sender_email: senderEmail,
          sender_name: senderName
        }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Error calling send-booking-confirmation function:', error);
        throw error;
      }

      console.log('Confirmation email sent successfully');
    } catch (error) {
      console.error('Error in sendConfirmationEmail:', error);
      throw error;
    }
  };

  const updateBookingStatus = async (id: string, status: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      const { error } = await supabase
        .from('transfer_bookings')
        .update({ booking_status: status })
        .eq('id', id);

      if (error) throw error;
      await fetchBookings(); // Refresh the list
    } catch (err) {
      console.error('Error updating booking status:', err);
      throw new Error('Error updating booking status');
    }
  };

  const deleteBooking = async (id: string) => {
    try {
      const { error } = await supabase
        .from('transfer_bookings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchBookings(); // Refresh the list
    } catch (err) {
      console.error('Error deleting booking:', err);
      throw new Error('Error deleting booking');
    }
  };

  const sendBookingConfirmationEmail = async (bookingData: any) => {
    try {
      console.log('Sending booking confirmation email manually:', bookingData.booking_number);
      await sendConfirmationEmail(bookingData);
    } catch (error) {
      console.error('Error sending booking confirmation email:', error);
      throw error;
    }
  };

  return {
    bookings,
    loading,
    error,
    createBooking,
    updateBookingStatus,
    deleteBooking,
    refetch: fetchBookings,
    sendBookingConfirmationEmail,
  };
};